import { useEffect, useState } from "react";
import { Trophy } from "lucide-react";
import { api } from "../lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

function Board({ title, rows, value, testId }) {
  return (
    <div className="space-y-3" data-testid={testId}>
      <h2 className="brut-h2">{title}</h2>
      {rows.length === 0 && <p className="text-sm text-zinc-500">No catches here yet. Be the first.</p>}
      <ol className="space-y-2">
        {rows.map((r) => (
          <li key={`${r.rank}-${r.first_name}`} data-testid={`${testId}-row-${r.rank}`}
            className={`flex items-center gap-4 p-3 border-2 ${r.is_me ? "border-[#FF4D00] bg-[#FF4D00]/10" : "border-[#27272A] bg-[#141417]"}`}>
            <span className={`font-display text-xl w-10 h-10 flex items-center justify-center border-2 border-black ${r.rank === 1 ? "bg-[#FF4D00] text-black" : "bg-[#1E1E24]"}`}>{r.rank}</span>
            <span className="flex-1 font-bold truncate">{r.first_name}{r.is_me && <span className="text-[#FF4D00] text-xs ml-2">YOU</span>}</span>
            <span className="text-right">{value(r)}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}

function Boards({ species }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    api.get("/leaderboard", { params: species ? { species } : {} }).then((r) => setData(r.data)).catch(() => setData({ most_catches: [], biggest_catch: [] }));
  }, [species]);
  if (!data) return <p className="text-zinc-500" data-testid="leaderboard-loading">Loading…</p>;
  return (
    <div className="space-y-10">
      <Board title="Biggest catch" rows={data.biggest_catch} testId={species ? "species-biggest" : "overall-biggest"}
        value={(r) => <><b className="font-display text-lg">{r.length_in}"</b>{!species && <span className="block text-xs text-zinc-400">{r.species}</span>}</>} />
      <Board title="Most catches" rows={data.most_catches} testId={species ? "species-most" : "overall-most"}
        value={(r) => <b className="font-display text-lg">{r.count}</b>} />
    </div>
  );
}

export default function Leaderboard() {
  const [speciesList, setSpeciesList] = useState([]);
  const [species, setSpecies] = useState("");
  useEffect(() => {
    api.get("/leaderboard/species").then((r) => { setSpeciesList(r.data); if (r.data[0]) setSpecies(r.data[0].key); }).catch(() => {});
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <h1 className="font-display text-4xl sm:text-5xl uppercase leading-none">Leader<br /><span className="text-[#FF4D00]">board</span></h1>
        <Trophy size={40} className="text-[#FF4D00]" />
      </div>
      <Tabs defaultValue="overall">
        <TabsList className="grid grid-cols-2 w-full h-auto p-0 rounded-none bg-transparent border-2 border-[#F4F4F5]">
          <TabsTrigger value="overall" data-testid="leaderboard-tab-overall"
            className="rounded-none py-3 font-bold uppercase data-[state=active]:bg-[#FF4D00] data-[state=active]:text-black">Overall</TabsTrigger>
          <TabsTrigger value="species" data-testid="leaderboard-tab-species"
            className="rounded-none py-3 font-bold uppercase data-[state=active]:bg-[#FF4D00] data-[state=active]:text-black">By species</TabsTrigger>
        </TabsList>
        <TabsContent value="overall" className="mt-8"><Boards /></TabsContent>
        <TabsContent value="species" className="mt-8 space-y-8">
          {speciesList.length === 0 ? <p className="text-sm text-zinc-500" data-testid="leaderboard-species-empty">No species logged yet.</p> : (
            <>
              <Select value={species} onValueChange={setSpecies}>
                <SelectTrigger className="brut-input h-[52px] rounded-none" data-testid="leaderboard-species-select"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-none border-2 border-[#FF4D00] max-h-72">
                  {speciesList.map((s) => <SelectItem key={s.key} value={s.key} data-testid={`leaderboard-species-option-${s.key}`}>{s.name} ({s.count})</SelectItem>)}
                </SelectContent>
              </Select>
              {species && <Boards species={species} />}
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
